import { 
  GlobeAltIcon, 
  LightBulbIcon, 
  UserGroupIcon, 
  TrophyIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import ImageWithFallback from '../components/ui/ImageWithFallback'
import { getPlaceholderImage, getLocalImage, heroImages } from '../utils/images'

const features = [
  {
    name: 'Global Innovation Hub',
    description: 'Leading AI innovation platform bringing together global talent to build the future.',
    icon: GlobeAltIcon,
    image: getLocalImage('globalcompetition'),
  },
  {
    name: 'Real-World Impact',
    description: 'Solve actual challenges across industries using cutting-edge AI and machine learning technologies.',
    icon: LightBulbIcon,
    image: getLocalImage('realworldproblems'),
  },
  {
    name: 'Expert Community',
    description: 'Connect with industry leaders, researchers, and fellow innovators from top organizations worldwide.',
    icon: UserGroupIcon,
    image: getLocalImage('experts'),
  },
  {
    name: 'Recognition & Rewards',
    description: 'Gain recognition for your innovative AI solutions and compete for substantial rewards.',
    icon: TrophyIcon,
    image: getLocalImage('awards'),
  },
]

const participants = [
  {
    name: 'Researchers',
    description: 'Academic researchers pushing the boundaries of AI across multiple domains.',
    icon: AcademicCapIcon,
    count: '2,500+',
    image: getLocalImage('experts'),
  },
  {
    name: 'Students',
    description: 'Talented students from universities worldwide showcasing their skills.',
    icon: AcademicCapIcon,
    count: '5,000+',
    image: getLocalImage('ai'),
  },
  {
    name: 'Corporations',
    description: 'Leading companies developing innovative solutions for smart buildings.',
    icon: BuildingOfficeIcon,
    count: '500+',
    image: getLocalImage('realworldproblems'),
  },
]

const organizers = [
  {
    name: 'Electrical and Mechanical Services Department (EMSD)',
    role: 'Main Organizer',
    description: 'Hong Kong government department responsible for electrical and mechanical services regulation and development.',
    logo: getLocalImage('realworldproblems'),
  },
  {
    name: 'Guangdong Provincial Association for Science and Technology (GPAST)',
    role: 'Co-Organizer',
    description: 'Leading science and technology organization promoting innovation in Guangdong Province.',
    logo: getLocalImage('ai'),
  }
]

const achievements = [
  { label: 'Global Participants', value: '10,000+' },
  { label: 'Countries Represented', value: '50+' },
  { label: 'Total Prize Pool', value: '$500K+' },
  { label: 'AI Solutions Submitted', value: '2,500+' },
  { label: 'Industry Partners', value: '100+' },
  { label: 'Academic Institutions', value: '200+' },
]

const timeline = [
  {
    year: '2020',
    title: 'Initiative Launch',
    description: 'ZovaLabs was founded to democratize AI innovation and empower the next generation of technology leaders.',
  },
  {
    year: '2021',
    title: 'Platform Launch',
    description: 'Launched our AI innovation platform with 1,000 developers and researchers from 20 countries.',
  },
  {
    year: '2022',
    title: 'Global Expansion',
    description: 'Expanded to include participants from 35 countries with diverse AI innovation projects.',
  },
  {
    year: '2023',
    title: 'Industry Partnership',
    description: 'Formed strategic partnerships with leading technology companies and academic institutions.',
  },
  {
    year: '2024',
    title: 'Innovation Hub',
    description: 'Now serving as a global hub for AI innovation with over 10,000 active community members.',
  },
]

export default function AboutPage() {
  return (
    <div className="bg-dark-900">
      {/* Hero section with background image */}
      <div className="relative bg-gradient-to-br from-dark-800 to-dark-900 overflow-hidden">
        <div className="absolute inset-0">
          <ImageWithFallback
            src={heroImages.about}
            alt="AI and Building Technology"
            className="w-full h-full object-cover opacity-20"
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 600'%3E%3Crect width='1200' height='600' fill='%233b82f6'/%3E%3C/svg%3E"
          />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-100 sm:text-5xl md:text-6xl relative">
              <span className="block">About</span>
              <span className="block text-gradient-gold gold-text-shadow">ZovaLabs</span>
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 gold-divider w-32"></div>
            </h1>
            <p className="mt-8 max-w-3xl mx-auto text-xl text-gray-300">
              to build the future with AI. We bring together innovators from around the globe 
              to collaborate, learn, and create groundbreaking solutions that shape tomorrow.
            </p>
            <div className="mt-10">
              <Link
                to="/challenges"
                className="inline-flex items-center px-8 py-4 border border-transparent text-base font-medium rounded-md text-dark-900 bg-gradient-gold hover:shadow-xl hover:gold-glow transition-all duration-200 transform hover:scale-105 relative overflow-hidden group"
              >
                <span className="relative z-10">Explore Challenges</span>
                <ArrowRightIcon className="ml-2 h-5 w-5 relative z-10 group-hover:translate-x-1 transition-transform duration-200" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mission section with side-by-side layout */}
      <div className="py-16 bg-dark-800 border-t border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-center">
            <div>
              <h2 className="text-base text-primary-400 font-semibold tracking-wide uppercase relative">
                Our Mission
                <div className="absolute -bottom-1 left-0 w-16 h-0.5 bg-gradient-gold"></div>
              </h2>
              <p className="mt-6 text-3xl leading-8 font-extrabold tracking-tight text-gray-100 sm:text-4xl">
                Advancing AI in Building Technology
              </p>
              <p className="mt-4 text-lg text-gray-300">
                We're dedicated to fostering innovation in artificial intelligence applications for building 
                electrical and mechanical services. Our platform connects global talent to solve real-world 
                challenges in smart buildings, energy efficiency, and facility management.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  'Promote AI innovation in building technology',
                  'Connect global talent and expertise',
                  'Solve real-world industry challenges',
                  'Foster collaboration between academia and industry'
                ].map((item, index) => (
                  <div key={index} className="flex items-center hover-gold-lift" style={{animationDelay: `${index * 0.1}s`}}>
                    <CheckCircleIcon className="h-5 w-5 text-primary-400 mr-3 animate-gold-pulse" style={{animationDelay: `${index * 0.2}s`}} />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-10 lg:mt-0">
              <ImageWithFallback
                src={getLocalImage('ai')}
                alt="Our Mission"
                className="w-full rounded-lg shadow-lg"
                fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23f3f4f6'/%3E%3Ctext x='300' y='200' text-anchor='middle' dy='.3em' fill='%236b7280' font-family='Arial, sans-serif' font-size='24'%3EOur Mission%3C/text%3E%3C/svg%3E"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features section with images */}
      <div className="py-16 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-100 sm:text-4xl relative">
              Why Join Our Challenge?
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 gold-divider w-32"></div>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-300">
              Discover the unique opportunities and benefits of participating in our global AI competition.
            </p>
          </div>
          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {features.map((feature, index) => (
              <div key={feature.name} className="relative group hover-gold-lift">
                <div className="relative overflow-hidden rounded-lg bg-dark-800 shadow-xl border border-primary-500/20 gold-corner-accent">
                  <ImageWithFallback
                    src={feature.image}
                    alt={feature.name}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    fallbackSrc={`data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f3f4f6'/%3E%3Ctext x='200' y='150' text-anchor='middle' dy='.3em' fill='%236b7280' font-family='Arial, sans-serif' font-size='16'%3E${feature.name}%3C/text%3E%3C/svg%3E`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-2 right-2 gold-dot" style={{animationDelay: `${index * 0.3}s`}}></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center mb-2">
                      <div className="flex items-center justify-center h-8 w-8 rounded-md bg-gradient-gold text-dark-900 mr-3 shadow-glow animate-gold-pulse" style={{animationDelay: `${index * 0.2}s`}}>
                        <feature.icon className="h-4 w-4" />
                      </div>
                      <h3 className="text-lg font-medium text-white group-hover:text-gradient-gold transition-colors duration-200">
                        {feature.name}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-200">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Participants section */}
      <div className="py-16 bg-dark-800 border-t border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-100 sm:text-4xl relative">
              Who Participates?
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 gold-divider w-32"></div>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-300">
              Our community brings together diverse talent from around the world.
            </p>
          </div>
          <div className="mt-12 grid gap-8 lg:grid-cols-3">
            {participants.map((participant, index) => (
              <div key={participant.name} className="text-center group hover-gold-lift">
                <div className="relative overflow-hidden rounded-lg border border-primary-500/20 gold-corner-accent">
                  <ImageWithFallback
                    src={participant.image}
                    alt={participant.name}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    fallbackSrc={`data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23334155'/%3E%3Ctext x='150' y='100' text-anchor='middle' dy='.3em' fill='%23f59e0b' font-family='Arial, sans-serif' font-size='16'%3E${participant.name}%3C/text%3E%3C/svg%3E`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent" />
                  <div className="absolute top-2 right-2 gold-dot" style={{animationDelay: `${index * 0.3}s`}}></div>
                  <div className="absolute bottom-4 left-4 right-4 text-center">
                    <div className="flex justify-center mb-2">
                      <div className="p-2 bg-gradient-gold bg-opacity-20 rounded-full backdrop-blur-sm animate-gold-pulse" style={{animationDelay: `${index * 0.2}s`}}>
                        <participant.icon className="h-6 w-6 text-gold-400" />
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gradient-gold gold-text-shadow">{participant.count}</div>
                  </div>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-100 group-hover:text-gradient-gold transition-colors duration-200">{participant.name}</h3>
                  <p className="mt-2 text-base text-gray-300">{participant.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Achievements section */}
      <div className="py-16 bg-dark-900 relative">
        <div className="absolute inset-0 bg-dark-pattern opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-100 sm:text-4xl relative">
              Our Impact
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 gold-divider w-32"></div>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-300">
              See the global reach and impact of our AI challenge platform.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-6">
            {achievements.map((achievement, index) => (
              <div key={achievement.label} className="text-center hover-gold-lift" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="text-3xl font-bold text-gradient-gold gold-text-shadow animate-gold-pulse" style={{animationDelay: `${index * 0.2}s`}}>{achievement.value}</div>
                <div className="mt-2 text-sm text-gray-400">{achievement.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline section */}
      <div className="py-16 bg-dark-800 border-t border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-100 sm:text-4xl relative">
              Our Journey
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 gold-divider w-32"></div>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-300">
              From a simple idea to a global platform connecting AI innovators worldwide.
            </p>
          </div>
          <div className="mt-12">
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-gradient-gold opacity-60"></div>
              <div className="space-y-12">
                {timeline.map((item, index) => (
                  <div key={item.year} className="relative">
                    <div className="flex items-center">
                      <div className="flex-1 pr-8 text-right">
                        {index % 2 === 0 && (
                          <div className="hover-gold-lift">
                            <div className="text-lg font-semibold text-primary-400">{item.year}</div>
                            <h3 className="text-xl font-bold text-gray-100">{item.title}</h3>
                            <p className="mt-2 text-gray-300">{item.description}</p>
                          </div>
                        )}
                      </div>
                      <div className="relative flex items-center justify-center w-8 h-8 bg-gradient-gold rounded-full shadow-glow animate-gold-pulse" style={{animationDelay: `${index * 0.3}s`}}>
                        <div className="w-3 h-3 bg-dark-900 rounded-full"></div>
                      </div>
                      <div className="flex-1 pl-8">
                        {index % 2 === 1 && (
                          <div className="hover-gold-lift">
                            <div className="text-lg font-semibold text-primary-400">{item.year}</div>
                            <h3 className="text-xl font-bold text-gray-100">{item.title}</h3>
                            <p className="mt-2 text-gray-300">{item.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organizers section */}
      <div className="py-16 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-100 sm:text-4xl relative">
              Our Organizers
              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 gold-divider w-32"></div>
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-300">
              Leading organizations committed to advancing AI in building technology.
            </p>
          </div>
          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {organizers.map((organizer, index) => (
              <div key={organizer.name} className="bg-dark-800 rounded-lg p-8 text-center border border-primary-500/20 hover-gold-lift gold-corner-accent">
                <div className="absolute top-2 right-2 gold-dot" style={{animationDelay: `${index * 0.3}s`}}></div>
                <ImageWithFallback
                  src={organizer.logo}
                  alt={organizer.name}
                  className="h-16 mx-auto mb-4 animate-float" style={{animationDelay: `${index * 0.5}s`}}
                  fallbackSrc={`data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 100'%3E%3Crect width='200' height='100' fill='%23334155' stroke='%23f59e0b' stroke-width='2'/%3E%3Ctext x='100' y='50' text-anchor='middle' dy='.3em' fill='%23f59e0b' font-family='Arial, sans-serif' font-size='12'%3ELogo%3C/text%3E%3C/svg%3E`}
                />
                <h3 className="text-lg font-medium text-gray-100">{organizer.name}</h3>
                <p className="mt-1 text-sm font-medium text-primary-400">{organizer.role}</p>
                <p className="mt-2 text-sm text-gray-300">{organizer.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA section */}
      <div className="relative bg-dark-800 border-t border-dark-700">
        <div className="absolute inset-0">
          <ImageWithFallback
            src={getLocalImage('globalcompetition')}
            alt="Join us background"
            className="w-full h-full object-cover opacity-20"
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 400'%3E%3Crect width='1200' height='400' fill='%23334155'/%3E%3C/svg%3E"
          />
        </div>
        <div className="absolute inset-0 bg-dark-pattern opacity-30"></div>
        <div className="relative max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-gray-100 sm:text-4xl relative">
            <span className="block">Ready to join the challenge?</span>
            <span className="block text-gradient-gold gold-text-shadow">Be part of the AI revolution.</span>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 gold-divider w-32"></div>
          </h2>
          <p className="mt-6 text-lg leading-6 text-gray-300">
            Join thousands of innovators from around the world and help shape the future of smart buildings.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row sm:justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-md text-dark-900 bg-gradient-gold hover:shadow-xl hover:gold-glow transition-all duration-200 transform hover:scale-105 relative overflow-hidden group"
            >
              <span className="relative z-10">Get Started</span>
              <ArrowRightIcon className="ml-2 h-5 w-5 relative z-10 group-hover:translate-x-1 transition-transform duration-200" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            </Link>
            <Link
              to="/challenges"
              className="inline-flex items-center justify-center px-8 py-4 border border-primary-500 text-base font-medium rounded-md text-primary-400 hover:bg-primary-500 hover:text-dark-900 transition-all duration-200 gold-border-animated"
            >
              View Challenges
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}